import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Tabs,
  Tab,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { projectService, Project } from '../services/projectService';
import ProjectCard from '../components/ProjectCard';
import { useThemeMode } from '../contexts/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DashboardIssuer = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      // TODO: Filter projects by user_id when wallet connection is implemented
      const response = await projectService.getProjects();
      setProjects(response.projects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getProjectsByStatus = (status: string) => {
    return projects.filter(project => project.status === status);
  };

  const getStatusStats = () => {
    const stats = {
      draft: getProjectsByStatus('draft').length,
      submitted: getProjectsByStatus('submitted').length,
      approved: getProjectsByStatus('approved').length,
      tokenized: getProjectsByStatus('tokenized').length,
      rejected: getProjectsByStatus('rejected').length,
    };

    return [
      {
        label: 'Draft Projects',
        value: stats.draft,
        icon: MoneyIcon,
        color: 'text.secondary',
      },
      {
        label: 'Submitted',
        value: stats.submitted,
        icon: ScheduleIcon,
        color: 'warning.main',
      },
      {
        label: 'Approved',
        value: stats.approved,
        icon: CheckCircleIcon,
        color: 'info.main',
      },
      {
        label: 'Tokenized',
        value: stats.tokenized,
        icon: TrendingUpIcon,
        color: 'success.main',
      },
    ];
  };



  const renderProjectGrid = (projectList: Project[]) => {
    if (loading) {
      return (
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" height={24} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (projectList.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {tabValue === 0 && 'You haven\'t created any projects yet.'}
              {tabValue === 1 && 'No projects are currently submitted for review.'}
              {tabValue === 2 && 'No projects have been approved yet.'}
              {tabValue === 3 && 'No projects have been tokenized yet.'}
            </Typography>
            {tabValue === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/submit')}
                sx={{
                  background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                  },
                }}
              >
                Create New Project
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }

    return (
      <Grid container spacing={3}>
        {projectList.map((project, index) => (
          <Grid item xs={12} md={4} key={project.id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProjectCard
                project={project}
                onInvest={() => {}}
              />
            </motion.div>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1,
                background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Issuer Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Manage your tokenized asset projects
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/submit')}
            sx={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
              boxShadow: '0 4px 15px rgba(30, 64, 175, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                boxShadow: '0 6px 20px rgba(30, 64, 175, 0.4)',
              },
            }}
          >
            Submit New Project
          </Button>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              background: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
              border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {getStatusStats().map((stat, index) => (
            <Grid item xs={12} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <stat.icon
                        sx={{
                          fontSize: '2rem',
                          color: stat.color,
                          mr: 2,
                        }}
                      />
                      <Box>
                        <Typography variant="h4" fontWeight={700} color={stat.color}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Projects Tabs */}
        <Card
          sx={{
            background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: '#3B82F6',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    background: 'linear-gradient(90deg, #3B82F6, #0EA5E9)',
                  },
                }}
              >
                <Tab label="Draft" />
                <Tab label="Submitted" />
                <Tab label="Approved" />
                <Tab label="Tokenized" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {renderProjectGrid(getProjectsByStatus('draft'))}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderProjectGrid(getProjectsByStatus('submitted'))}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderProjectGrid(getProjectsByStatus('approved'))}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {renderProjectGrid(getProjectsByStatus('tokenized'))}
            </TabPanel>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default DashboardIssuer; 